import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ArrowRight,
  ArrowUpRight,
  ChevronDown,
  Clock,
  Facebook,
  Instagram,
  Mail,
  MapPin,
  Menu,
  MessageCircle,
  Music2,
  Phone,
  ShieldCheck,
  Sparkles,
  Star,
  X,
  Youtube,
  Zap,
} from "lucide-react";
import OngroundStoreIcon from "../OngroundStoreIcon";
import {
  resolveOngroundAppLink,
  resolveThemeLogo,
  resolveVenueSocialLink,
} from "../publicWebsiteContent";
import {
  ARENA_REFERENCE_FAQS,
  ARENA_REFERENCE_GROUNDS,
  ARENA_REFERENCE_IMAGES,
  ARENA_REFERENCE_STATS,
  ARENA_REFERENCE_TESTIMONIALS,
  ARENA_REFERENCE_VENUE,
  getArenaReferenceGroundImage,
  resolveArenaApp,
  resolveArenaCollection,
  resolveArenaCopy,
  resolveArenaPositiveNumber,
  resolveArenaText,
} from "./arenaFallbacks";



const getText = (value) => String(value || "").trim();

const getSafeExternalUrl = (value) => {
  const input = getText(value);

  if (!input) {
    return "";
  }

  try {
    const parsedUrl = new URL(input);

    if (!["http:", "https:"].includes(parsedUrl.protocol)) {
      return "";
    }

    return parsedUrl.toString();
  } catch {
    return "";
  }
};

const getExternalUrlLabel = (value) => {
  const safeUrl = getSafeExternalUrl(value);

  if (!safeUrl) {
    return "";
  }

  try {
    return new URL(safeUrl).hostname.replace(/^www\./i, "");
  } catch {
    return safeUrl;
  }
};

const formatLabel = (value) =>
  getText(value)
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (character) => character.toUpperCase());

const normalizeLookup = (value) => getText(value).toLowerCase();

const formatTime = (value) => {
  const input = getText(value);

  if (!/^\d{1,2}:\d{2}$/.test(input)) {
    return input;
  }

  const [rawHour, minute] = input.split(":");
  const hour = Number(rawHour);

  if (!Number.isFinite(hour)) {
    return input;
  }

  const period = hour >= 12 ? "PM" : "AM";
  const displayHour = hour % 12 || 12;

  return `${displayHour}:${minute} ${period}`;
};

const formatPrice = (amount, currency) => {
  const numericAmount = Number(amount);

  if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
    return "";
  }

  const normalizedCurrency = getText(currency).toUpperCase();

  try {
    if (!normalizedCurrency) {
      return new Intl.NumberFormat(undefined, {
        maximumFractionDigits: 0,
      }).format(numericAmount);
    }

    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: normalizedCurrency,
      maximumFractionDigits: 0,
    }).format(numericAmount);
  } catch {
    return normalizedCurrency
      ? `${normalizedCurrency} ${numericAmount}`
      : String(numericAmount);
  }
};

const getSportMetadata = (ground = {}) => ({
  tagline:
    getText(ground?.referenceTagline) ||
    formatLabel(ground?.type || ground?.name),
});

const getGroundGalleryImage = (venue, ground) => {
  const groundGallery = venue?.media?.groundGallery;

  if (!groundGallery || typeof groundGallery !== "object") {
    return "";
  }

  const candidateKeys = [
    ground?._id,
    ground?.id,
    ground?.type,
    ground?.name,
    ground?.sportName,
    ground?.sport?.name,
  ]
    .filter(Boolean)
    .map(String);

  for (const key of candidateKeys) {
    const exactValue = groundGallery[key];

    if (Array.isArray(exactValue) && exactValue[0]) {
      return exactValue[0];
    }

    const matchedEntry = Object.entries(groundGallery).find(
      ([entryKey]) => normalizeLookup(entryKey) === normalizeLookup(key),
    );

    if (Array.isArray(matchedEntry?.[1]) && matchedEntry[1][0]) {
      return matchedEntry[1][0];
    }
  }

  return "";
};

const getGroundApiImage = ({ ground, venue, gallery, index }) => {
  const groundImageUrl = getText(ground?.mainImageUrl);

  if (groundImageUrl) {
    return groundImageUrl;
  }

  const groundGalleryImage = getGroundGalleryImage(venue, ground);

  if (groundGalleryImage) {
    return groundGalleryImage;
  }

  if (gallery.length) {
    return gallery[index % gallery.length] || "";
  }

  return getText(venue?.media?.bannerImage);
};

const getGroundUnitLabel = (unit) => {
  const normalized = normalizeLookup(unit);

  return normalized ? `/${normalized}` : "";
};

const ArenaEyebrow = ({ children }) => (
  <span className="pva-eyebrow">{children}</span>
);

const ArenaSection = ({ children, className = "", id }) => (
  <section id={id} className={`pva-section ${className}`.trim()}>
    {children}
  </section>
);

const ArenaInfoCard = ({ icon: Icon, value }) => (
  <div className="pva-info-card">
    <span className="pva-info-card-icon" aria-hidden="true">
      <Icon />
    </span>

    <span>
      <strong>{value}</strong>
    </span>
  </div>
);

const ArenaHeader = ({
  venueName,
  logoUrl,
  city,
  address,
  phone,
  hours,
  bookingEnabled,
  ctaLabel,
  openBooking,
}) => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const headerRef = useRef(null);

  useEffect(() => {
    const ownerWindow =
      headerRef.current?.ownerDocument?.defaultView;

    if (!ownerWindow) {
      return undefined;
    }

    const handleScroll = () => {
      setScrolled(ownerWindow.scrollY > 40);
    };

    handleScroll();

    ownerWindow.addEventListener(
      "scroll",
      handleScroll,
      {
        passive: true,
      },
    );

    return () => {
      ownerWindow.removeEventListener(
        "scroll",
        handleScroll,
      );
    };
  }, []);

  return (
    <>
      <header
        ref={headerRef}
        className={`pva-header ${scrolled ? "is-scrolled" : ""}`}
      >
        <nav className="pva-header-inner" aria-label="Venue website navigation">
          <a className="pva-brand" href="#top" aria-label={venueName}>
            {logoUrl ? (
              <span className="pva-brand-logo" aria-hidden="true">
                <img src={logoUrl} alt="" />
              </span>
            ) : (
              <span className="pva-brand-mark" aria-hidden="true">
                {venueName.charAt(0).toUpperCase()}
                <i />
              </span>
            )}

            <span className="pva-brand-copy">
              <strong>{venueName}</strong>
              {city && <small>{city}</small>}
            </span>
          </a>

          <div className="pva-header-actions">
            {bookingEnabled && ctaLabel && (
              <button
                type="button"
                className="pva-header-cta"
                onClick={() => openBooking?.()}
              >
                {ctaLabel}
                <ArrowUpRight />
              </button>
            )}

            <button
              type="button"
              className="pva-menu-trigger"
              aria-label="Open menu"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen(true)}
            >
              <Menu />
            </button>
          </div>
        </nav>
      </header>

      {mobileOpen && (
        <div
          className="pva-drawer-layer"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setMobileOpen(false);
            }
          }}
        >
          <aside
            className="pva-mobile-drawer"
            role="dialog"
            aria-modal="true"
            aria-label="Venue menu"
          >
            <button
              type="button"
              className="pva-drawer-close"
              aria-label="Close menu"
              onClick={() => setMobileOpen(false)}
            >
              <X />
            </button>

            <a
              className="pva-drawer-brand"
              href="#top"
              onClick={() => setMobileOpen(false)}
            >
              {logoUrl ? (
                <span className="pva-brand-logo" aria-hidden="true">
                  <img src={logoUrl} alt="" />
                </span>
              ) : (
                <span className="pva-brand-mark" aria-hidden="true">
                  {venueName.charAt(0).toUpperCase()}
                </span>
              )}

              <span className="pva-brand-copy">
                <strong>{venueName}</strong>
                {city && <small>{city}</small>}
              </span>
            </a>

            <div className="pva-drawer-divider" />

            {(address || phone || hours) && (
              <div className="pva-drawer-details">
                {address && (
                  <p>
                    <MapPin aria-hidden="true" />
                    <span>{address}</span>
                  </p>
                )}

                {phone && (
                  <p>
                    <Phone aria-hidden="true" />
                    <span>{phone}</span>
                  </p>
                )}

                {hours && (
                  <p>
                    <Clock aria-hidden="true" />
                    <span>{hours}</span>
                  </p>
                )}
              </div>
            )}

            {bookingEnabled && ctaLabel && (
              <button
                type="button"
                className="pva-drawer-cta"
                onClick={() => {
                  setMobileOpen(false);
                  openBooking?.();
                }}
              >
                {ctaLabel}
              </button>
            )}
          </aside>
        </div>
      )}
    </>
  );
};

const ArenaFooter = ({
  venueName,
  logoUrl,
  city,
  address,
  phone,
  email,
  hours,
  tagline,
  bookingEnabled,
  openBooking,
  copy = {},
  social = {},
  showGrounds,
  showAbout,
  showContact,
}) => {
  const socialItems = [
    {
      label: "Instagram",
      icon: Instagram,
      url: resolveVenueSocialLink(
        social.instagramUrl,
        "instagram",
      ),
    },
    {
      label: "Facebook",
      icon: Facebook,
      url: resolveVenueSocialLink(
        social.facebookUrl,
        "facebook",
      ),
    },
    {
      label: "TikTok",
      icon: Music2,
      url: resolveVenueSocialLink(
        social.tiktokUrl,
        "tiktok",
      ),
    },
    {
      label: "WhatsApp",
      icon: MessageCircle,
      url: resolveVenueSocialLink(
        social.whatsappUrl,
        "whatsapp",
      ),
    },
    {
      label: "YouTube",
      icon: Youtube,
      url: getSafeExternalUrl(social.youtubeUrl),
    },
  ].filter((item) => item.url);

  const quickLinks = [
    {
      key: "grounds",
      href: "#grounds",
      label: getText(copy.footerBookLabel),
      visible: showGrounds,
    },
    {
      key: "about",
      href: "#about",
      label: getText(copy.footerAboutLabel),
      visible: showAbout,
    },
    {
      key: "contact",
      href: "#contact",
      label: getText(copy.footerContactLabel),
      visible: showContact,
    },
  ].filter((item) => item.visible && item.label);

  const startBookingLabel = getText(
    copy.footerStartBookingLabel,
  );

  const hasQuickLinks =
    quickLinks.length > 0 ||
    (bookingEnabled && startBookingLabel);

  const hasVisitDetails =
    address ||
    phone ||
    email ||
    hours ||
    getText(copy.footerVisitTitle);

  const copyrightSuffix = getText(
    copy.footerCopyrightSuffix,
  );

  const poweredByText = getText(
    copy.footerPoweredByText,
  );

  return (
    <footer className="pva-footer">
      <div className="pva-footer-inner">
        <div className="pva-footer-grid">
          <div className="pva-footer-brand-column">
            {venueName && (
              <a className="pva-footer-brand" href="#top">
                {logoUrl ? (
                  <span className="pva-brand-logo" aria-hidden="true">
                    <img src={logoUrl} alt="" />
                  </span>
                ) : (
                  <span
                    className="pva-brand-mark"
                    aria-hidden="true"
                  >
                    {venueName.charAt(0).toUpperCase()}
                    <i />
                  </span>
                )}

                <span className="pva-brand-copy">
                  <strong>{venueName}</strong>
                  {city && <small>{city}</small>}
                </span>
              </a>
            )}

            {tagline && <p>{tagline}</p>}

            {socialItems.length > 0 && (
              <div className="pva-social-links">
                {socialItems.map(
                  ({ label, icon: Icon, url }) => (
                    <a
                      key={label}
                      href={url}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={label}
                    >
                      <Icon />
                    </a>
                  ),
                )}
              </div>
            )}
          </div>

          {hasQuickLinks && (
            <div className="pva-footer-links">
              {copy.footerQuickLinksTitle && (
                <h4>{copy.footerQuickLinksTitle}</h4>
              )}

              {quickLinks.map((item) => (
                <a key={item.key} href={item.href}>
                  {item.label}
                </a>
              ))}

              {bookingEnabled && startBookingLabel && (
                <button
                  type="button"
                  onClick={() => openBooking?.()}
                >
                  {startBookingLabel}
                </button>
              )}
            </div>
          )}

          {hasVisitDetails && (
            <div className="pva-footer-visit">
              {copy.footerVisitTitle && (
                <h4>{copy.footerVisitTitle}</h4>
              )}

              <div className="pva-footer-contact-list">
                {address && (
                  <a
                    className="pva-footer-contact"
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <span className="pva-footer-contact-icon" aria-hidden="true">
                      <MapPin />
                    </span>
                    <span className="pva-footer-contact-copy">
                      <small>Location</small>
                      <strong>{address}</strong>
                    </span>
                    <ArrowUpRight />
                  </a>
                )}

                {phone && (
                  <a
                    className="pva-footer-contact"
                    href={`tel:${String(phone).replace(/[^+\d]/g, "")}`}
                  >
                    <span className="pva-footer-contact-icon" aria-hidden="true">
                      <Phone />
                    </span>
                    <span className="pva-footer-contact-copy">
                      <small>Phone</small>
                      <strong>{phone}</strong>
                    </span>
                    <ArrowUpRight />
                  </a>
                )}

                {email && (
                  <a
                    className="pva-footer-contact"
                    href={`mailto:${email}`}
                  >
                    <span className="pva-footer-contact-icon" aria-hidden="true">
                      <Mail />
                    </span>
                    <span className="pva-footer-contact-copy">
                      <small>Email</small>
                      <strong>{email}</strong>
                    </span>
                    <ArrowUpRight />
                  </a>
                )}
              </div>

              {hours && (
                <div className="pva-hours-badge">
                  <i />
                  {hours}
                </div>
              )}
            </div>
          )}
        </div>

        {(venueName || poweredByText) && (
          <div className="pva-footer-bottom">
            {venueName && (
              <p>
                © {new Date().getFullYear()} {venueName}
                {copyrightSuffix
                  ? ` ${copyrightSuffix}`
                  : ""}
              </p>
            )}

            {poweredByText && (
              <a
                href="https://getonground.com"
                target="_blank"
                rel="noreferrer"
                aria-label="Visit Onground website"
                style={{ color: 'inherit', textDecoration: 'none' }}
              >
                {poweredByText}
              </a>
            )}
          </div>
        )}
      </div>
    </footer>
  );
};

const ArenaAppPromotion = ({
  venueName,
  city,
  primaryGround,
  bookingEnabled,
  openBooking,
  app = {},
}) => {
  const primaryPrice = formatPrice(
    primaryGround?.basePrice ??
      primaryGround?.price ??
      primaryGround?.pricing?.basePrice,
    primaryGround?.currency ||
      primaryGround?.pricing?.currency,
  );

  const features = Array.isArray(app.features)
    ? app.features.map(getText).filter(Boolean)
    : [];

  const googlePlayUrl = resolveOngroundAppLink(
    app.googlePlayUrl,
    "googlePlay",
  );

  const appStoreUrl = resolveOngroundAppLink(
    app.appStoreUrl,
    "appStore",
  );

  const storeLinks = [
    {
      key: "google-play",
      platform: "googlePlay",
      url: googlePlayUrl,
      label: getExternalUrlLabel(googlePlayUrl),
    },
    {
      key: "app-store",
      platform: "appStore",
      url: appStoreUrl,
      label: getExternalUrlLabel(appStoreUrl),
    },
  ].filter((item) => item.url && item.label);

  const title = getText(app.title);
  const highlightedTitle = getText(
    app.highlightedTitle,
  );

  const hasPhonePreview =
    primaryGround ||
    getText(app.greeting) ||
    getText(app.customerName) ||
    getText(app.featuredLabel) ||
    getText(app.featuredTime) ||
    getText(app.slotsLabel) ||
    getText(app.confirmLabel) ||
    getText(app.ratingText) ||
    getText(app.reviewsText) ||
    getText(app.instantTitle) ||
    getText(app.instantSubtitle);

  const hasPromotionContent =
    getText(app.badge) ||
    title ||
    highlightedTitle ||
    getText(app.description) ||
    features.length > 0 ||
    storeLinks.length > 0 ||
    getText(app.proofText) ||
    getText(app.ctaLabel) ||
    hasPhonePreview;

  if (!app.enabled || !hasPromotionContent) {
    return null;
  }

  return (
    <section className="pva-app-section">
      <div
        className="pva-app-background"
        aria-hidden="true"
      >
        <i />
        <b />
        <span />
      </div>

      <div className="pva-app-inner">
        <div className="pva-app-grid">
          <div className="pva-app-copy">
            {app.badge && (
              <span className="pva-app-badge">
                <i />
                {app.badge}
              </span>
            )}

            {(title || highlightedTitle) && (
              <h2>
                {title}
                {title && highlightedTitle && <br />}
                {highlightedTitle && (
                  <span>{highlightedTitle}</span>
                )}
              </h2>
            )}

            {app.description && <p>{app.description}</p>}

            {features.length > 0 && (
              <div className="pva-app-features">
                {features.map((feature, index) => (
                  <span key={`${feature}-${index}`}>
                    {feature}
                  </span>
                ))}
              </div>
            )}

            {storeLinks.length > 0 && (
              <div className="pva-store-buttons">
                {storeLinks.map((store) => (
                  <a
                    key={store.key}
                    href={store.url}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <OngroundStoreIcon
                      platform={store.platform}
                      monochromeColor="#ffffff"
                    />

                    <span>
                      <strong>{store.label}</strong>
                    </span>
                  </a>
                ))}
              </div>
            )}

            {app.proofText && (
              <div className="pva-app-proof">
                <span>{app.proofText}</span>
              </div>
            )}

            {bookingEnabled && app.ctaLabel && (
              <button
                type="button"
                className="pva-app-booking-cta"
                onClick={() =>
                  openBooking?.(primaryGround || null)
                }
              >
                {app.ctaLabel}
                <ArrowRight />
              </button>
            )}
          </div>

          {hasPhonePreview && (
            <div className="pva-phone-area">
              <div className="pva-phone-glow" />

              <div className="pva-phone">
                <div className="pva-phone-content">
                  {(app.greeting || app.customerName) && (
                    <div className="pva-phone-header">
                      <span>
                        {app.greeting && (
                          <small>{app.greeting}</small>
                        )}

                        {app.customerName && (
                          <strong>
                            {app.customerName}
                          </strong>
                        )}
                      </span>

                      <i />
                    </div>
                  )}

                  {(primaryGround ||
                    app.featuredLabel ||
                    app.featuredTime ||
                    primaryPrice) && (
                    <div className="pva-phone-feature">
                      {app.featuredLabel && (
                        <small>{app.featuredLabel}</small>
                      )}

                      {primaryGround?.name && (
                        <strong>
                          {primaryGround.name}
                        </strong>
                      )}

                      {(venueName || city) && (
                        <span>
                          {[venueName, city]
                            .filter(Boolean)
                            .join(" · ")}
                        </span>
                      )}

                      {(app.featuredTime ||
                        primaryPrice) && (
                        <div>
                          {app.featuredTime && (
                            <b>{app.featuredTime}</b>
                          )}

                          {primaryPrice && (
                            <strong>
                              {primaryPrice}
                            </strong>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {app.slotsLabel && (
                    <small className="pva-phone-slot-label">
                      {app.slotsLabel}
                    </small>
                  )}

                  {app.confirmLabel && (
                    <div className="pva-phone-confirm">
                      {app.confirmLabel}
                    </div>
                  )}
                </div>
              </div>

              {(app.ratingText || app.reviewsText) && (
                <div className="pva-floating-rating">
                  <Star aria-hidden="true" />

                  <div>
                    {app.ratingText && (
                      <strong>
                        {app.ratingText}
                      </strong>
                    )}

                    {app.reviewsText && (
                      <small>{app.reviewsText}</small>
                    )}
                  </div>
                </div>
              )}

              {(app.instantTitle ||
                app.instantSubtitle) && (
                <div className="pva-floating-instant">
                  <Zap aria-hidden="true" />

                  <div>
                    {app.instantTitle && (
                      <strong>
                        {app.instantTitle}
                      </strong>
                    )}

                    {app.instantSubtitle && (
                      <small>
                        {app.instantSubtitle}
                      </small>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

const ArenaTheme = ({
  venue,
  grounds = [],
  website,
  backgroundStyle = "clean",
  primaryColor = "#33A444",
  accentColor = "#111827",
  heroImage = "",
  ctaLabel = "",
  openBooking,
  themeLayout = "arena",
  selectedThemeLayout = "arena",
  previewMode = false,
}) => {
  const [openFaq, setOpenFaq] = useState(null);
  const rootRef = useRef(null);
  const resolvedArenaDark =
    String(accentColor || "").trim().toLowerCase() === "#111827"
      ? "#0a0f1a"
      : accentColor;

  const arena =
    website?.arena &&
    typeof website.arena === "object"
      ? website.arena
      : {};

  const configuredCopy =
    arena.copy && typeof arena.copy === "object"
      ? arena.copy
      : {};

  const copy = resolveArenaCopy(configuredCopy);

  const social =
    arena.social && typeof arena.social === "object"
      ? arena.social
      : {};

  const configuredApp =
    arena.app && typeof arena.app === "object"
      ? arena.app
      : {};

  const app = resolveArenaApp(configuredApp);

  const configuredTestimonials = (
    Array.isArray(arena.testimonials)
      ? arena.testimonials
      : []
  )
    .map((testimonial) => {
      const numericRating = Number(testimonial?.rating);

      return {
        name: getText(testimonial?.name),
        role: getText(testimonial?.role),
        text: getText(testimonial?.text),
        rating:
          Number.isFinite(numericRating) && numericRating > 0
            ? Math.min(5, Math.floor(numericRating))
            : 0,
      };
    })
    .filter(
      (testimonial) =>
        testimonial.name ||
        testimonial.role ||
        testimonial.text ||
        testimonial.rating > 0,
    );

  const testimonials = resolveArenaCollection(
    configuredTestimonials,
    [],
    ARENA_REFERENCE_TESTIMONIALS,
  );

  const configuredFaqs = (
    Array.isArray(arena.faqs) ? arena.faqs : []
  )
    .map((faq) => ({
      question: getText(faq?.question),
      answer: getText(faq?.answer),
    }))
    .filter((faq) => faq.question && faq.answer);

  const faqs = resolveArenaCollection(
    configuredFaqs,
    [],
    ARENA_REFERENCE_FAQS,
  );

  const configuredStats = (
    Array.isArray(arena.stats) ? arena.stats : []
  )
    .map((stat) => ({
      value: getText(stat?.value),
      label: getText(stat?.label),
    }))
    .filter((stat) => stat.value && stat.label);

  const stats = resolveArenaCollection(
    configuredStats,
    [],
    ARENA_REFERENCE_STATS,
  );

  const rating = Math.min(
    5,
    resolveArenaPositiveNumber(
      arena.rating,
      0,
      ARENA_REFERENCE_VENUE.rating,
    ),
  );

  const reviewCount = Math.floor(
    resolveArenaPositiveNumber(
      arena.reviewCount,
      0,
      ARENA_REFERENCE_VENUE.reviewCount,
    ),
  );

  const venueName = resolveArenaText(
    "",
    venue?.name,
    ARENA_REFERENCE_VENUE.name,
  );

  const venueLogoUrl = resolveThemeLogo(
    website,
    "arena",
    "",
  );

  const apiCity = getText(
    venue?.location?.city || venue?.city,
  );

  const city = resolveArenaText(
    "",
    apiCity,
    ARENA_REFERENCE_VENUE.city,
  );

  const country = apiCity
    ? getText(venue?.location?.country || venue?.country)
    : "";

  const address = resolveArenaText(
    "",
    venue?.location?.address,
    ARENA_REFERENCE_VENUE.address,
  );

  const phone = resolveArenaText(
    "",
    venue?.contact?.e164 ||
      venue?.contact?.nationalNumber,
    ARENA_REFERENCE_VENUE.phone,
  );

  const email = resolveArenaText(
    arena.contactEmail,
    venue?.contact?.email || venue?.email,
    ARENA_REFERENCE_VENUE.email,
  );

  const openingTime = getText(
    venue?.policies?.openTime,
  );

  const closingTime = getText(
    venue?.policies?.closeTime,
  );

  const timeRange =
    openingTime && closingTime
      ? `${formatTime(openingTime)} – ${formatTime(
          closingTime,
        )}`
      : formatTime(openingTime || closingTime);

  const hours = timeRange
    ? [
        getText(copy.hoursPrefix),
        timeRange,
        getText(copy.hoursSuffix),
      ]
        .filter(Boolean)
        .join(" ")
    : ARENA_REFERENCE_VENUE.hours;

  const gallery = useMemo(
    () =>
      Array.isArray(venue?.media?.gallery)
        ? venue.media.gallery.filter(Boolean)
        : [],
    [venue?.media?.gallery],
  );

  const hasApiGrounds =
    Array.isArray(grounds) && grounds.length > 0;

  const sourceGrounds = hasApiGrounds
    ? grounds
    : ARENA_REFERENCE_GROUNDS;

  const resolvedGrounds = useMemo(
    () =>
      sourceGrounds.map((ground, index) => {
        const metadata = getSportMetadata(ground);
        const arenaApiImage = hasApiGrounds
          ? getGroundApiImage({
              ground,
              venue,
              gallery,
              index,
            })
          : "";

        return {
          ...ground,
          arenaTagline: metadata.tagline,
          arenaApiImage,
          arenaImage:
            arenaApiImage ||
            getArenaReferenceGroundImage(ground),
          arenaPrice: formatPrice(
            ground?.basePrice ??
              ground?.price ??
              ground?.pricing?.basePrice,
            ground?.currency ||
              ground?.pricing?.currency ||
              venue?.pricing?.currency,
          ),
          arenaUnit: getGroundUnitLabel(ground?.unit),
          arenaReferenceOnly: !hasApiGrounds,
        };
      }),
    [gallery, hasApiGrounds, sourceGrounds, venue],
  );

  const bookingEnabled =
    website?.booking?.enabled !== false;

  const sections = website?.sections || {};

  const canBook =
    bookingEnabled &&
    hasApiGrounds &&
    resolvedGrounds.length > 0;

  const effectiveCtaLabel = getText(ctaLabel);

  const apiGroundImages = resolvedGrounds
    .map((ground) => getText(ground?.arenaApiImage))
    .filter(Boolean);

  const firstApiGroundImage = apiGroundImages[0] || "";

  const heroSource =
    getText(heroImage) ||
    getText(venue?.media?.bannerImage) ||
    gallery[0] ||
    firstApiGroundImage ||
    ARENA_REFERENCE_IMAGES.hero;

  const howItWorksImage =
    gallery[1] ||
    apiGroundImages[1] ||
    firstApiGroundImage ||
    ARENA_REFERENCE_IMAGES.howItWorks;

  const locationImage =
    gallery[2] ||
    apiGroundImages[2] ||
    firstApiGroundImage ||
    ARENA_REFERENCE_IMAGES.location;

  const aboutImages = [
    ...new Set(
      [
        getText(heroImage),
        getText(venue?.media?.bannerImage),
        ...gallery.slice(0, 5),
        ...apiGroundImages,
        ...ARENA_REFERENCE_IMAGES.about,
      ].filter(Boolean),
    ),
  ].slice(0, 3);

  const effectiveHeadline = resolveArenaText(
    website?.hero?.headline,
    venue?.name,
    ARENA_REFERENCE_VENUE.heroHeadline,
  );

  const effectiveSubheadline = resolveArenaText(
    website?.hero?.subheadline,
    venue?.shortDescription,
    ARENA_REFERENCE_VENUE.heroSubheadline,
  );

  const shortDescription = resolveArenaText(
    "",
    venue?.shortDescription,
    ARENA_REFERENCE_VENUE.aboutLead,
  );

  const fullDescription = resolveArenaText(
    "",
    venue?.fullDescription,
    ARENA_REFERENCE_VENUE.aboutBody,
  );

  const footerTagline = resolveArenaText(
    "",
    venue?.shortDescription,
    ARENA_REFERENCE_VENUE.tagline,
  );

  const googleMapsUrl = getSafeExternalUrl(
    venue?.googleMapsUrl,
  );

  const locationLabel = [city, country]
    .filter(Boolean)
    .join(", ");

  const heroBadge = getText(copy.heroBadge);

  const hasRating = rating > 0;
  const hasReviewCount = reviewCount > 0;

  const bookingSteps = [
    {
      icon: Zap,
      title: getText(copy.bookingStepOneTitle),
      description: getText(
        copy.bookingStepOneDescription,
      ),
    },
    {
      icon: Clock,
      title: getText(copy.bookingStepTwoTitle),
      description: getText(
        copy.bookingStepTwoDescription,
      ),
    },
    {
      icon: ShieldCheck,
      title: getText(copy.bookingStepThreeTitle),
      description: getText(
        copy.bookingStepThreeDescription,
      ),
    },
  ].filter((step) => step.title || step.description);

  const hasHeroMeta =
    hasRating ||
    hasReviewCount ||
    locationLabel ||
    hours;

  const bookingSectionEnabled =
    sections.booking !== false;

  const hasBookingCopy = [
    copy.bookingEyebrow,
    copy.bookingTitle,
    copy.bookingDescription,
    copy.bookingButtonLabel,
  ].some((value) => getText(value)) ||
    bookingSteps.length > 0;

  const showGallery = sections.gallery !== false;

  const showHeroSection =
    sections.hero !== false &&
    Boolean(
      heroSource ||
        heroBadge ||
        effectiveHeadline ||
        effectiveSubheadline ||
        hasHeroMeta ||
        (canBook && effectiveCtaLabel),
    );

  const showGroundsSection =
    sections.grounds !== false &&
    Boolean(
      resolvedGrounds.length > 0 ||
        getText(copy.emptyGroundsLabel),
    );

  const showBookingSection =
    bookingSectionEnabled && hasBookingCopy;

  const showTestimonialsSection =
    sections.about !== false &&
    testimonials.length > 0;

  const showAboutSection =
    sections.about !== false &&
    Boolean(
      getText(copy.aboutEyebrow) ||
        getText(copy.aboutTitle) ||
        shortDescription ||
        fullDescription ||
        (showGallery && aboutImages.length > 0),
    );

  const showStatsSection =
    sections.about !== false &&
    stats.length > 0;

  const showLocationSection =
    sections.location !== false &&
    Boolean(
      getText(copy.locationEyebrow) ||
        getText(copy.locationContactLabel) ||
        address ||
        hours ||
        phone ||
        email ||
        locationImage,
    );

  const showContactSection =
    sections.contact !== false &&
    Boolean(
      getText(copy.contactEyebrow) ||
        getText(copy.contactTitle) ||
        getText(copy.contactDescription) ||
        getText(copy.contactOpenLabel) ||
        getText(copy.contactOpenDescription) ||
        phone ||
        email ||
        address ||
        hours,
    );

  const showPoliciesSection =
    sections.policies !== false &&
    faqs.length > 0;

  return (
    <main
      ref={rootRef}
      id="top"
      className={[
        `pv-page pv-theme-${themeLayout} pv-bg-${backgroundStyle} pva-page`,
        previewMode
          ? "pva-page-preview"
          : "",
      ]
        .filter(Boolean)
        .join(" ")}
      data-theme={themeLayout}
      data-selected-theme={selectedThemeLayout}
      style={{
        "--pv-primary": primaryColor,
        "--pv-accent": accentColor,
        "--pva-primary": primaryColor,
        "--pva-accent": accentColor,
        "--pva-dark": resolvedArenaDark,
      }}
    >
      <ArenaHeader
        venueName={venueName}
        logoUrl={venueLogoUrl}
        city={city}
        address={address}
        phone={phone}
        hours={hours}
        bookingEnabled={canBook}
        ctaLabel={effectiveCtaLabel}
        openBooking={openBooking}
      />

           {showHeroSection && (
        <section className="pva-hero">
          <div className="pva-hero-media" aria-hidden="true">
            {heroSource && <img src={heroSource} alt="" />}
            <i />
            <b />
          </div>

          <div className="pva-hero-inner">
            {heroBadge && (
              <span className="pva-hero-badge">
                <Sparkles />
                {heroBadge}
              </span>
            )}

            {effectiveHeadline && (
              <h1>
                <span className="pva-hero-dynamic-headline">
                  {effectiveHeadline}
                </span>
              </h1>
            )}

            {effectiveSubheadline && (
              <p>{effectiveSubheadline}</p>
            )}

            {canBook && effectiveCtaLabel && (
              <button
                type="button"
                className="pva-primary-button"
                onClick={() => openBooking?.()}
              >
                {effectiveCtaLabel}
                <ArrowRight />
              </button>
            )}

            {hasHeroMeta && (
              <div className="pva-hero-meta">
                {(hasRating || hasReviewCount) && (
                  <span>
                    {hasRating && (
                      <Star className="is-star" />
                    )}

                    {hasRating && (
                      <strong>{rating}</strong>
                    )}

                    {hasRating && hasReviewCount && " · "}

                    {hasReviewCount && (
                      <span>
                        {reviewCount.toLocaleString()}
                      </span>
                    )}
                  </span>
                )}

                {locationLabel && (
                  <span>
                    <MapPin />
                    {locationLabel}
                  </span>
                )}

                {hours && (
                  <span>
                    <Clock />
                    {hours}
                  </span>
                )}
              </div>
            )}
          </div>
        </section>
      )}

            {showGroundsSection && (
        <ArenaSection id="grounds">
          {(copy.groundsEyebrow ||
            copy.groundsTitle ||
            copy.groundsDescription) && (
            <div className="pva-section-heading">
              <div>
                {copy.groundsEyebrow && (
                  <ArenaEyebrow>
                    {copy.groundsEyebrow}
                  </ArenaEyebrow>
                )}

                {copy.groundsTitle && (
                  <h2>{copy.groundsTitle}</h2>
                )}

                {copy.groundsDescription && (
                  <p>{copy.groundsDescription}</p>
                )}
              </div>
            </div>
          )}

          {resolvedGrounds.length > 0 ? (
            <div className="pva-ground-grid">
              {resolvedGrounds.map((ground, index) => {
                const groundName = getText(
                  ground?.name ||
                    formatLabel(ground?.type),
                );

                const capacity = getText(
                  ground?.capacity,
                );

                const capacityText = capacity
                  ? [
                      capacity,
                      getText(
                        copy.groundCapacitySuffix,
                      ),
                    ]
                      .filter(Boolean)
                      .join(" ")
                  : "";

                const hasPriceContent =
                  ground.arenaPrice ||
                  getText(copy.priceOnRequestLabel);

                const hasFooterContent =
                  hasPriceContent ||
                  (canBook &&
                    getText(copy.groundBookLabel));

                return (
                  <button
                    type="button"
                    className="pva-ground-card"
                    key={
                      ground?._id ||
                      `${groundName}-${index}`
                    }
                    onClick={() => {
                      if (!ground.arenaReferenceOnly) {
                        openBooking?.(ground);
                      }
                    }}
                    disabled={
                      !canBook || ground.arenaReferenceOnly
                    }
                  >
                    {ground.arenaImage && (
                      <span className="pva-ground-image">
                        <img
                          src={ground.arenaImage}
                          alt={groundName}
                          loading="lazy"
                        />
                      </span>
                    )}

                    <span className="pva-ground-content">
                      {(groundName || capacityText) && (
                        <span className="pva-ground-title-row">
                          {groundName && (
                            <strong>{groundName}</strong>
                          )}

                          {capacityText && (
                            <small>{capacityText}</small>
                          )}
                        </span>
                      )}

                      {ground.arenaTagline && (
                        <span className="pva-ground-tagline">
                          {ground.arenaTagline}
                        </span>
                      )}

                      {hasFooterContent && (
                        <span className="pva-ground-footer">
                          {hasPriceContent && (
                            <span className="pva-ground-price">
                              {ground.arenaPrice ? (
                                <>
                                  {copy.pricePrefix && (
                                    <small>
                                      {copy.pricePrefix}
                                    </small>
                                  )}

                                  <strong>
                                    {ground.arenaPrice}
                                  </strong>

                                  {ground.arenaUnit && (
                                    <small>
                                      {ground.arenaUnit}
                                    </small>
                                  )}
                                </>
                              ) : (
                                <strong>
                                  {
                                    copy.priceOnRequestLabel
                                  }
                                </strong>
                              )}
                            </span>
                          )}

                          {canBook &&
                            copy.groundBookLabel && (
                              <span className="pva-ground-book">
                                {copy.groundBookLabel}
                                <ArrowRight />
                              </span>
                            )}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          ) : (
            copy.emptyGroundsLabel && (
              <div className="pva-empty-state">
                {copy.emptyGroundsLabel}
              </div>
            )
          )}
        </ArenaSection>
      )}

            {showBookingSection && (
        <ArenaSection>
          <div className="pva-how-grid">
            <div>
              {copy.bookingEyebrow && (
                <ArenaEyebrow>
                  {copy.bookingEyebrow}
                </ArenaEyebrow>
              )}

              {copy.bookingTitle && (
                <h2>{copy.bookingTitle}</h2>
              )}

              {copy.bookingDescription && (
                <p>{copy.bookingDescription}</p>
              )}

              {bookingSteps.length > 0 && (
                <ol className="pva-how-list">
                  {bookingSteps.map(
                    (
                      {
                        icon: Icon,
                        title,
                        description,
                      },
                      index,
                    ) => (
                      <li key={index}>
                        <span>{index + 1}</span>

                        <div>
                          <Icon aria-hidden="true" />

                          {title && (
                            <strong>{title}</strong>
                          )}

                          {description && (
                            <p>{description}</p>
                          )}
                        </div>
                      </li>
                    ),
                  )}
                </ol>
              )}

              {canBook &&
                copy.bookingButtonLabel && (
                  <button
                    type="button"
                    className="pva-primary-button"
                    onClick={() =>
                      document
                        .getElementById("grounds")
                        ?.scrollIntoView({
                          behavior: "smooth",
                          block: "start",
                        })
                    }
                  >
                    {copy.bookingButtonLabel}
                    <ArrowRight />
                  </button>
                )}
            </div>

            {howItWorksImage && (
              <div className="pva-how-image">
                <i aria-hidden="true" />

                <img
                  src={howItWorksImage}
                  alt={`${venueName} booking preview`}
                  loading="lazy"
                />
              </div>
            )}
          </div>
        </ArenaSection>
      )}

            {showTestimonialsSection && (
        <ArenaSection>
          {(copy.testimonialsEyebrow ||
            copy.testimonialsTitle) && (
            <div className="pva-section-heading">
              <div>
                {copy.testimonialsEyebrow && (
                  <ArenaEyebrow>
                    {copy.testimonialsEyebrow}
                  </ArenaEyebrow>
                )}

                {copy.testimonialsTitle && (
                  <h2>{copy.testimonialsTitle}</h2>
                )}
              </div>
            </div>
          )}

          <div className="pva-testimonial-grid">
            {testimonials.map(
              (testimonial, testimonialIndex) => (
                <article
                  key={`${testimonial.name}-${testimonialIndex}`}
                  className="pva-testimonial-card"
                >
                  {testimonial.rating > 0 && (
                    <div className="pva-stars">
                      {Array.from({
                        length: testimonial.rating,
                      }).map((_, starIndex) => (
                        <Star key={starIndex} />
                      ))}
                    </div>
                  )}

                  {testimonial.text && (
                    <blockquote>
                      {testimonial.text}
                    </blockquote>
                  )}

                  {(testimonial.name ||
                    testimonial.role) && (
                    <footer>
                      {testimonial.name && (
                        <span>
                          {testimonial.name
                            .charAt(0)
                            .toUpperCase()}
                        </span>
                      )}

                      <div>
                        {testimonial.name && (
                          <strong>
                            {testimonial.name}
                          </strong>
                        )}

                        {testimonial.role && (
                          <small>
                            {testimonial.role}
                          </small>
                        )}
                      </div>
                    </footer>
                  )}
                </article>
              ),
            )}
          </div>
        </ArenaSection>
      )}

      {showAboutSection && (
        <ArenaSection
          className="pva-about-section"
          id="about"
        >
          <div className="pva-about-grid">
            {(copy.aboutEyebrow ||
              copy.aboutTitle ||
              shortDescription ||
              fullDescription) && (
              <div>
                {copy.aboutEyebrow && (
                  <ArenaEyebrow>
                    {copy.aboutEyebrow}
                  </ArenaEyebrow>
                )}

                {copy.aboutTitle && (
                  <h2>{copy.aboutTitle}</h2>
                )}

                {shortDescription && (
                  <p className="pva-about-lead">
                    {shortDescription}
                  </p>
                )}

                {fullDescription && (
                  <p>{fullDescription}</p>
                )}
              </div>
            )}

            {showGallery &&
              aboutImages.length > 0 && (
                <div className="pva-about-gallery">
                  <img
                    src={aboutImages[0]}
                    alt={`${venueName} venue`}
                    loading="lazy"
                  />

                  {aboutImages.length > 1 && (
                    <div>
                      {aboutImages
                        .slice(1, 3)
                        .map((image, index) => (
                          <img
                            key={image}
                            src={image}
                            alt={`${venueName} facility ${
                              index + 1
                            }`}
                            loading="lazy"
                          />
                        ))}
                    </div>
                  )}
                </div>
              )}
          </div>
        </ArenaSection>
      )}

      {showStatsSection && (
        <ArenaSection className="pva-stat-section">
          <div className="pva-stat-grid">
            {stats.map((stat, index) => (
              <article
                key={`${stat.label}-${index}`}
              >
                <strong>{stat.value}</strong>
                <span>{stat.label}</span>
              </article>
            ))}
          </div>
        </ArenaSection>
      )}

            {showLocationSection && (
        <ArenaSection>
          <div className="pva-location-card">
            <div>
              {copy.locationEyebrow && (
                <ArenaEyebrow>
                  {copy.locationEyebrow}
                </ArenaEyebrow>
              )}

              {address && <h2>{address}</h2>}

              {hours && <p>{hours}</p>}

              {locationImage && (
                <div className="pva-location-image">
                  {googleMapsUrl ? (
                    <a
                      href={googleMapsUrl}
                      target="_blank"
                      rel="noreferrer"
                      aria-label={`Open ${venueName} in maps`}
                    >
                      <img
                        src={locationImage}
                        alt={`${venueName} location`}
                        loading="lazy"
                      />
                    </a>
                  ) : (
                    <img
                      src={locationImage}
                      alt={`${venueName} location`}
                      loading="lazy"
                    />
                  )}
                </div>
              )}
            </div>

            {(copy.locationContactLabel ||
              phone ||
              email ||
              hours) && (
              <aside>
                {copy.locationContactLabel && (
                  <small>
                    {copy.locationContactLabel}
                  </small>
                )}

                {phone && <strong>{phone}</strong>}

                {email && <span>{email}</span>}

                {hours && (
                  <div className="pva-hours-list">
                    <p>
                      <strong>{hours}</strong>
                    </p>
                  </div>
                )}
              </aside>
            )}
          </div>
        </ArenaSection>
      )}

            {showContactSection && (
        <ArenaSection
          className="pva-contact-section"
          id="contact"
        >
          {(copy.contactEyebrow ||
            copy.contactTitle ||
            copy.contactDescription) && (
            <div className="pva-contact-heading">
              {copy.contactEyebrow && (
                <ArenaEyebrow>
                  {copy.contactEyebrow}
                </ArenaEyebrow>
              )}

              {copy.contactTitle && (
                <h2>{copy.contactTitle}</h2>
              )}

              {copy.contactDescription && (
                <p>{copy.contactDescription}</p>
              )}
            </div>
          )}

          {(phone ||
            email ||
            address ||
            hours ||
            copy.contactOpenLabel ||
            copy.contactOpenDescription) && (
            <div className="pva-contact-grid">
              <aside className="pva-contact-aside">
                {phone && (
                  <ArenaInfoCard
                    icon={Phone}
                    value={phone}
                  />
                )}

                {email && (
                  <ArenaInfoCard
                    icon={Mail}
                    value={email}
                  />
                )}

                {address && (
                  <ArenaInfoCard
                    icon={MapPin}
                    value={address}
                  />
                )}

                {(copy.contactOpenLabel ||
                  hours ||
                  copy.contactOpenDescription) && (
                  <div className="pva-open-card">
                    {copy.contactOpenLabel && (
                      <small>
                        {copy.contactOpenLabel}
                      </small>
                    )}

                    {hours && <strong>{hours}</strong>}

                    {copy.contactOpenDescription && (
                      <p>
                        {copy.contactOpenDescription}
                      </p>
                    )}
                  </div>
                )}
              </aside>
            </div>
          )}
        </ArenaSection>
      )}

            {showPoliciesSection && (
        <ArenaSection>
          <div className="pva-faq">
            {copy.faqEyebrow && (
              <ArenaEyebrow>
                {copy.faqEyebrow}
              </ArenaEyebrow>
            )}

            {copy.faqTitle && (
              <h2>{copy.faqTitle}</h2>
            )}

            <div className="pva-accordion">
              {faqs.map((faq, index) => {
                const expanded = openFaq === index;

                return (
                  <article
                    key={`${faq.question}-${index}`}
                    className={
                      expanded ? "is-open" : ""
                    }
                  >
                    <button
                      type="button"
                      aria-expanded={expanded}
                      onClick={() =>
                        setOpenFaq(
                          expanded ? null : index,
                        )
                      }
                    >
                      <span>{faq.question}</span>
                      <ChevronDown />
                    </button>

                    {expanded && (
                      <p>{faq.answer}</p>
                    )}
                  </article>
                );
              })}
            </div>
          </div>
        </ArenaSection>
      )}

      {bookingSectionEnabled && (
        <ArenaAppPromotion
          venueName={venueName}
          city={city}
          primaryGround={resolvedGrounds[0] || null}
                    bookingEnabled={canBook}
          openBooking={openBooking}
          app={app}
        />
      )}

      <ArenaFooter
        venueName={venueName}
        logoUrl={venueLogoUrl}
        city={city}
        address={address}
        phone={phone}
        email={email}
        hours={hours}
        tagline={footerTagline}
        bookingEnabled={canBook}
        openBooking={openBooking}
        copy={copy}
        social={social}
        showGrounds={
          showGroundsSection &&
          resolvedGrounds.length > 0
        }
        showAbout={showAboutSection}
        showContact={showContactSection}
      />

      {canBook && effectiveCtaLabel && (
        <button
          type="button"
          className="pva-mobile-booking"
          onClick={() => openBooking?.()}
        >
          <Clock />
          {effectiveCtaLabel}
        </button>
      )}
    </main>
  );
};

export default ArenaTheme;
